/**
 * @author       david.loehr@zumper.com (David Loehr)
 * @copyright    Copyright (c) 2022, Zumper
 * @description  RelatedPropertiesMonetizedLazyLoadableSection.jsx
 */

/** @jsx jsx */
import { css, jsx } from '@emotion/react'
import { BREAKPOINT_480 } from '@zcode-ui/themes'
import { createLazyLoadableSection } from '@zcode/lazy-loadable-section'
import { ErrorFallback, LoadingFallback } from '@zcode/nr/es/shared/components/Fallback'
import type { ErrorBoundaryProps } from 'react-error-boundary'
import { logger } from '../../logger'

const fallbackStyle = css`
  height: 745px;
  @media (min-width: ${BREAKPOINT_480}) {
    height: 777px;
  }
`

const onRenderError: ErrorBoundaryProps['onError'] = (error, info) => {
  logger.error('render error for RelatedPropertiesMonetizedLazyLoadableSection', info, error)
}

const onLoadingError = (error: unknown) => {
  logger.error('loading error for RelatedPropertiesMonetizedLazyLoadableSection', error)
}

const loadFn = /* #__LOADABLE__ */ async () =>
  await import(/* webpackChunkName: "RelatedPropertiesMonetized" */ './RelatedPropertiesMonetized')

const message = `Sorry, we couldn't load similar apartments...`

const handleReload = async () => await RelatedPropertiesMonetizedLazyLoadableSection.reload()

const RelatedPropertiesMonetizedErrorFallback = () => (
  <ErrorFallback css={fallbackStyle} message={message} onClickReload={handleReload} />
)
export const RelatedPropertiesMonetizedLoadingFallback = () => (
  <LoadingFallback css={fallbackStyle} name="similar section" />
)

const options = {
  errorFallback: RelatedPropertiesMonetizedErrorFallback,
  loadingFallback: RelatedPropertiesMonetizedLoadingFallback,
  onRenderError,
  onLoadingError,
}

export const RelatedPropertiesMonetizedLazyLoadableSection = createLazyLoadableSection(
  loadFn,
  options
)
